import { motion, useReducedMotion } from 'framer-motion'
import { useRef, type ReactNode } from 'react'

interface RevealProps {
  children: ReactNode
  delay?: number
  duration?: number
  y?: number
  className?: string
  /** Trigger only once when scrolling into view */
  once?: boolean
  /** Use as a div wrapper */
  as?: 'div' | 'section' | 'article' | 'li'
}

/**
 * Reveal — scroll-reveal animation wrapper.
 *
 * Safety contract: **content is always visible**. We never start at
 * opacity:0 — only a small `y` offset — so that if JS fails, the
 * IntersectionObserver never fires (full-page screenshots, crawlers,
 * headless rendering), or the user prefers reduced motion, the page
 * stays fully readable. The animation is purely additive polish.
 */
export default function Reveal({
  children,
  delay = 0,
  duration = 0.6,
  y = 16,
  className,
  once = true,
  as = 'div',
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useReducedMotion()
  const MotionTag = motion[as] as typeof motion.div

  // Reduced motion → no animation, render as-is.
  if (prefersReducedMotion) {
    return (
      <MotionTag ref={ref} className={className}>
        {children}
      </MotionTag>
    )
  }

  return (
    <MotionTag
      ref={ref}
      // IMPORTANT: keep opacity at 1 (the natural state) so the element is
      // visible immediately. Only animate the y offset on scroll-in.
      initial={{ opacity: 1, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.05, margin: '0px 0px -5% 0px' }}
      transition={{ duration, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </MotionTag>
  )
}